<template>
  <div class="flex flex-col h-full overflow-hidden">
    <ThreadsNav show-back :show-center="false">
      <template #right>
        <CrystalPowerCount />
      </template>
    </ThreadsNav>

    <div>
      <VanCellGroup inset>
        <VanCell required>
          <VanField
            :label="$t('adoption.adoptionTitle')"
            v-model="adoption.title"
            :placeholder="$t('Message.none')"
            class="p-0"
            input-align="right"
          />
        </VanCell>
        <VanCell required>
          <template #title>
            <div
              class="flex items-center justify-between gap-2"
              @click="clickSelectMate"
            >
              <div>伙伴</div>

              <div
                class="text-xs text-base-content/30 flex items-center"
                v-if="!adoption.id"
              >
                <span>{{ $t(`adoption.selectPartner`) }}</span>
                <i class="iconify mingcute--right-line w-6"></i>
              </div>
            </div>
          </template>
          <template #label v-if="adoption.mateId">
            <div class="w-[60px] relative flex flex-col gap-1 items-center">
              <button
                v-if="!adoption.id"
                class="bg-neutral w-5 h-5 rounded-full flex items-center justify-center absolute top-0 right-0 z-[1]"
                @click="adoption.mateId = undefined"
              >
                <i
                  class="iconify mdi--close text-xs w-3 text-neutral-content"
                />
              </button>

              <BaseImg
                :key="adoption.mateId"
                :mate="mateStore.mateIdMap.get(Number(adoption.mateId))"
                ratio="3:4"
              />
              <div class="text-xs line-clamp-1">
                {{ mateStore.mateIdMap.get(Number(adoption.mateId))?.name }}
              </div>
            </div>
          </template>
        </VanCell>
        <VanCell v-if="activeMate" required>
          <VanField
            class="p-0"
            :model-value="useMateTypeTextByMate(activeMate)"
            :label="$t('User.mate.basicInfo.type')"
            :placeholder="$t('Message.none')"
            :rules="[
              { required: true, message: $t('User.mate.basicInfo.rules.type') },
            ]"
            readonly
            @click="showMateTypePicker = true"
          >
            <template #right-icon>
              <i
                class="iconify mingcute--question-line cursor-pointer"
                @click.stop="clickShowMateTypeHelp"
              />
            </template>
          </VanField>
        </VanCell>
        <VanCell :title="$t(`adoption.aiAssisted`)" required>
          <template #right-icon>
            <van-switch v-model="adoption.isAiGenerated" :size="20" />
          </template>
        </VanCell>

        <VanCell :title="$t(`adoption.isListed`)" required v-if="adoption.id">
          <template #right-icon>
            <van-switch
              v-model="adoption.status"
              :active-value="AdoptionStatus.Available"
              :inactive-value="AdoptionStatus.Unlisted"
              :size="20"
            />
          </template>
        </VanCell>
        <VanCell required>
          <van-field
            v-model.number="adoption.powerCubes"
            type="number"
            :label="$t(`adoption.adoptionThreshold`)"
            class="p-0"
            input-align="right"
          >
            <template #right-icon>
              <PowerIcon />
            </template>
          </van-field>
        </VanCell>
      </VanCellGroup>
      <div class="flex justify-center gap-3">
        <button
          v-if="!adoption.uuid"
          class="btn btn-primary btn-wide mt-3"
          @click="clickCreateAdoption"
        >
          <PowerIcon />
          <span> 1 </span>
          <span> {{ $t(`adoption.publishAdoption`) }} </span>
        </button>
        <template v-else>
          <button class="btn btn-outline mt-3" @click="clickDeleteAdoption">
            删除
          </button>
          <button class="btn btn-primary mt-3" @click="clickUpdateAdoption">
            保存
          </button>
        </template>
      </div>
    </div>
    <VanPopup v-model:show="showMateTypePicker" position="bottom">
      <VanPicker
        :columns="MateTypeOptions"
        :v-model="activeMate?.type"
        @cancel="showMateTypePicker = false"
        @confirm="onTypePickerConfirm"
      />
    </VanPopup>
  </div>
</template>

<script setup lang="ts">
import {
  createOneMateAdoption,
  deleteOneMateAdoption,
  readOneMateAdoption,
  updateOneMateAdoption,
} from "@/apis/mate";
import { AdoptionStatus, MateType } from "@/enums/mateEnums";
import { copyToClipboard } from "@/modules/common";
import { useGlobalUser } from "@/stores/globalUser";
import { useMate } from "@/stores/mate";
import { useUI } from "@/stores/ui";
import { Mate, MateAdoption } from "@/types/entity";
import { useAllMateType, useMateTypeTextByMate } from "@/use/common";
import { routerBack } from "@/use/go";
import { AxiosError } from "axios";
import {
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import { PickerOption } from "vant/es";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

const mateStore = useMate();
const { t } = useI18n();
const route = useRoute();
const adoption = ref<MateAdoption>({
  isAiGenerated: false,
  powerCubes: 10,
} as MateAdoption);
const uiStore = useUI();
const activeMate = ref<Mate>();
const clickSelectMate = () => {
  if (adoption.value.id) return;
  uiStore.popMateSelector({
    mateOnly: true,
    switchMate: false,
    exclude: adoption.value.mateId ? [adoption.value.mateId] : [],
    callback: (mate: Mate) => {
      adoption.value.mateId = mate.id;
      activeMate.value = JSON.parse(JSON.stringify(mate));
      uiStore.showMateSelector = false;
    },
  });
};

const clickCreateAdoption = async () => {
  const { title, mateId, powerCubes } = adoption.value;
  if (!title) return showFailToast(t(`adoption.rules.title`));
  if (!mateId) return showFailToast(t(`adoption.rules.mate`));
  if (!powerCubes) return showFailToast(t(`adoption.rules.powerCube`));
  if (powerCubes < 10)
    return showFailToast(t(`adoption.rules.powerCubeTooSmall`));
  const loading = showLoadingToast(t(`Message.creating`));

  try {
    await mateStore.updateMateDetail(activeMate.value!.uuid, {
      type: Number(activeMate.value!.type),
    });
    await createOneMateAdoption(adoption.value);
    await useGlobalUser().refresh();
    loading.close();
    showSuccessToast({
      message: t(`Message.publishSuccess`),
      onClose: routerBack,
      forbidClick: true,
    });
  } catch (e: unknown) {
    loading.close();
    const error = e as AxiosError<{ message: string }>;

    if (
      error.response?.data?.message?.startsWith("最多只能同时发布") ||
      error.response?.data?.message?.startsWith("You can only post up to")
    ) {
      showConfirmDialog({
        message: "加入qq群私信'饼次元'申请OC创作者后，可提升发布领养数量",
        confirmButtonText: "复制 QQ 群号",
        cancelButtonText: "关闭",
      }).then(() => {
        copyToClipboard("795055136");
        showSuccessToast("复制 QQ 群号成功");
      });
    }
  }
};

const clickUpdateAdoption = async () => {
  const { id, title, mateId, powerCubes } = adoption.value;
  if (!title) return showFailToast(t(`adoption.rules.title`));
  if (!mateId) return showFailToast(t(`adoption.rules.mate`));
  if (!powerCubes) return showFailToast(t(`adoption.rules.powerCube`));
  const loading = showLoadingToast(t(`Message.updating`));

  try {
    await updateOneMateAdoption(id, {
      title,
      powerCubes,
      isAiGenerated: adoption.value.isAiGenerated,
      status: adoption.value.status,
    });
    loading.close();
    showSuccessToast({
      message: `更新领养成功`,
      onClose: routerBack,
      forbidClick: true,
    });
  } catch {
    loading.close();
  }
};

const clickDeleteAdoption = async () => {
  const { id } = adoption.value;
  try {
    await showConfirmDialog({
      title: `删除领养`,
      message: `确定删除领养吗？`,
      confirmButtonText: `确定`,
      cancelButtonText: `取消`,
    });
  } catch {
    return;
  }

  const loading = showLoadingToast(t(`Message.deleting`));
  try {
    await deleteOneMateAdoption(id);
    showSuccessToast({
      message: `删除领养成功`,
      onClose: routerBack,
      forbidClick: true,
    });
  } catch {
    loading.close();
  }
};

const showMateTypePicker = ref(false);
const MateTypeOptions = useAllMateType();

const onTypePickerConfirm = async ({ selectedOptions }: PickerOption) => {
  if (!activeMate.value) return;
  const newType = selectedOptions[0]?.value as MateType;

  activeMate.value.type = newType;
  showMateTypePicker.value = false;
};

const clickShowMateTypeHelp = () => {
  showConfirmDialog({
    message: "原创角色领养后将转移至领养人账号\n二创角色可被多次领养",
    confirmButtonText: "知道啦",
    showCancelButton: false,
  });
  console.log("clickShowMateTypeHelp");
};

onMounted(async () => {
  const mateId = route.query.mateId;
  const adoptionUuid = route.query.adoptionUuid;

  if (adoptionUuid) {
    const loading = showLoadingToast(t(`Message.loading`));
    try {
      const _adoption = await readOneMateAdoption(adoptionUuid as string);
      adoption.value = _adoption.item;
      activeMate.value = JSON.parse(JSON.stringify(_adoption.item.mate));
      loading.close();
    } catch {
      loading.close();
      routerBack();
    }
  } else if (mateId) {
    adoption.value.mateId = Number(mateId);
  }
});
</script>

<style scoped>
/* Styles specific to this component */
</style>
